import React, { useState } from 'react';
import { RiFilterLine, RiCheckLine, RiArrowDownSLine, RiCloseLine } from '@remixicon/react';

/**
 * QuoteFilters Component
 * Dropdown filter for quotes with per-status counts and colored badges
 *
 * @param {Object} props
 * @param {string} props.activeFilter - Currently active filter
 * @param {Function} props.onFilterChange - Callback when filter changes
 * @param {number} props.totalCount - Total count of quotes for current filter
 * @param {Object} props.statusCounts - Per-status counts { all, draft, sent, accepted, declined }
 */
const QuoteFilters = ({ activeFilter = 'all', onFilterChange, totalCount = 0, statusCounts = {} }) => {
  const [isOpen, setIsOpen] = useState(false);

  const filters = [
    { value: 'all', label: 'All', badgeColor: 'bg-gray-100 text-gray-600' },
    { value: 'draft', label: 'Draft', badgeColor: 'bg-gray-100 text-gray-600' },
    { value: 'pending_approval', label: 'Pending Approval', badgeColor: 'bg-amber-100 text-amber-700' },
    { value: 'sent', label: 'Sent', badgeColor: 'bg-blue-100 text-blue-700' },
    { value: 'viewed', label: 'Seen', badgeColor: 'bg-blue-100 text-blue-700' },
    { value: 'signed', label: 'Signed', badgeColor: 'bg-blue-100 text-blue-700' },
    { value: 'accepted', label: 'Paid', badgeColor: 'bg-green-100 text-green-700' },
    { value: 'tripsheet_pending_approval', label: 'Trip Sheet Pending Approval', badgeColor: 'bg-amber-100 text-amber-700' },
    { value: 'passengers_pending', label: 'Pending Passengers', badgeColor: 'bg-amber-100 text-amber-700' },
    { value: 'waiting_flight', label: 'Waiting Flight', badgeColor: 'bg-amber-100 text-amber-700' },
    { value: 'in_transit', label: 'In Transit', badgeColor: 'bg-amber-100 text-amber-700' },
    { value: 'completed', label: 'Completed', badgeColor: 'bg-green-100 text-green-700' },
    { value: 'diverted', label: 'Diverted', badgeColor: 'bg-orange-100 text-orange-700' },
    { value: 'expired', label: 'Expired', badgeColor: 'bg-gray-100 text-gray-500' },
    { value: 'cancelled', label: 'Cancelled', badgeColor: 'bg-red-100 text-red-600' },
    { value: 'declined', label: 'Declined', badgeColor: 'bg-red-100 text-red-600' },
    { value: 'rejected', label: 'Rejected', badgeColor: 'bg-red-100 text-red-600' },
  ];

  const getActiveFilterLabel = () => {
    const filter = filters.find((f) => f.value === activeFilter);
    return filter ? filter.label : 'All';
  };

  const handleSelect = (value) => {
    onFilterChange?.(value);
    setIsOpen(false);
  };

  return (
    <div className="relative w-full">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-5 py-2.5 bg-white rounded-full border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all"
      >
        <RiFilterLine className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">{getActiveFilterLabel()}</span>
        <span className="flex-1" />
        {totalCount > 0 && (
          <span className="px-2.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full min-w-[24px] text-center">
            {totalCount}
          </span>
        )}
        <RiArrowDownSLine className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute z-50 w-full mt-2 bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900">Filter Quotes</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <RiCloseLine className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="space-y-1">
              {filters.map((filter) => {
                const count = statusCounts[filter.value] || 0;
                const isActive = activeFilter === filter.value;

                return (
                  <button
                    key={filter.value}
                    onClick={() => handleSelect(filter.value)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-colors ${
                      isActive ? 'bg-gray-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className={`text-sm ${isActive ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                      {filter.label}
                    </span>
                    <div className="flex items-center gap-2">
                      {count > 0 && (
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full min-w-[24px] text-center ${filter.badgeColor}`}>
                          {count}
                        </span>
                      )}
                      {isActive && (
                        <RiCheckLine className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuoteFilters;
